import {KeyLabel} from '../../shared/domain/key-label.model';

// The backend's `UserRole` enum (vb-core) was reconciled on 2026-08-02/03 - see
// dxkulture-backend/docs/rbac-permission-matrix.md - collapsing the old 11-role model onto
// ADMIN / READ_ONLY_ADMIN / USER / READONLY_USER / PUBLISHER, then layering ADVERTISER / FINANCE
// (SRS-46) and SUPER_ADMIN (NSR-13) on top. That is the full set of roles the backend can now
// issue to a real user (see vb-core UserRole.java).
//
// CONTENT_OWNER / SALES_REPRESENTATIVE / CAMPAIGN_MANAGER / REV_OPS / DESIGNER no longer exist as
// backend roles (folded into USER or READONLY_USER; the DB migration deleted those rows outright -
// see 20260802-rbac-role-reconciliation.yml). They are kept here ONLY because `Principal.mask` /
// mask-select.component.ts reuse this enum as FE-only "preview as" persona values for the admin
// mask-switcher - a real user's `roles` (from the JWT/`/users/me`) will never contain them again.
// Do not add them back to an assignable-role picker (see `UserRoleKL()` below) or treat them as
// real authorities - see role-permission.service.ts and principal.utils.ts for how USER /
// READONLY_USER now carry forward the access those legacy roles used to grant.
export enum UserRole {
  ADMIN = 'ADMIN',
  SUPER_ADMIN = 'SUPER_ADMIN',
  READ_ONLY_ADMIN = 'READ_ONLY_ADMIN',
  USER = 'USER',
  READONLY_USER = 'READONLY_USER',
  PUBLISHER = 'PUBLISHER',
  ADVERTISER = 'ADVERTISER',
  FINANCE = 'FINANCE',
  /** @deprecated FE-only mask/preview-persona value - no longer a real backend role. */
  CONTENT_OWNER = 'CONTENT_OWNER',
  /** @deprecated FE-only mask/preview-persona value - folded into READONLY_USER on the backend. */
  SALES_REPRESENTATIVE = 'SALES_REPRESENTATIVE',
  /** @deprecated FE-only mask/preview-persona value - folded into USER on the backend. */
  CAMPAIGN_MANAGER = 'CAMPAIGN_MANAGER',
  /** @deprecated FE-only mask/preview-persona value - folded into READONLY_USER on the backend. */
  REV_OPS = 'REV_OPS',
  /** @deprecated FE-only mask/preview-persona value - folded into USER on the backend. */
  DESIGNER = 'DESIGNER',
}

export const UserRoleLabels = {
  ADMIN: 'Admin',
  SUPER_ADMIN: 'Super Admin',
  READ_ONLY_ADMIN: 'Read only Admin',
  USER: 'User',
  READONLY_USER: 'Read only User',
  PUBLISHER: 'Publisher',
  ADVERTISER: 'Advertiser',
  FINANCE: 'Finance',
  CONTENT_OWNER: 'Content owner',
  SALES_REPRESENTATIVE: 'Sales representative',
  CAMPAIGN_MANAGER: 'Campaign manager',
  REV_OPS: 'Rev Ops',
  DESIGNER: 'Designer',
};

/** Every UserRole value, including the deprecated FE-only mask personas - do not use for an assignable-role picker. */
export const UserRoleKL = () =>
  (Object.keys(UserRole).map(key => new KeyLabel((key as UserRole), UserRoleLabels[key])));

/** Roles the backend will actually accept on a create/update user payload - use this for any role picker. */
const ASSIGNABLE_ROLES = new Set<string>([
  UserRole.ADMIN, UserRole.SUPER_ADMIN, UserRole.READ_ONLY_ADMIN,
  UserRole.USER, UserRole.READONLY_USER, UserRole.PUBLISHER,
  UserRole.ADVERTISER, UserRole.FINANCE,
]);

export const AssignableUserRoleKL = () =>
  UserRoleKL().filter(kl => ASSIGNABLE_ROLES.has(kl.key));
